import React, { useEffect, useRef, useState } from "react";
import { getCustomStoryOptions } from "../../../api/customAPI";
import { WordFilter } from "../../../utils/wordFilter/WordFilter";
import "../../../styles/components/selectbook/SelectOption.scss";
import useAppTranslation from "../../../hooks/useAppTranslation";
import { getLocalizedAssetPath } from "../../../utils/getLocalizedLogo";

export default function SelectOption({
  onNext,
  onPrev,
  storyId,
  selectedOptions,
  setSelectedOptions,
  onAlert,
  onSelectSound,
  onNextSound,
  onPrevSound,
}) {
  const { t, language } = useAppTranslation(
    "components/selectbook/selectOption",
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [optionList, setOptionList] = useState([]);
  const [customInputIdx, setCustomInputIdx] = useState(null);
  const [customInputs, setCustomInputs] = useState({});
  const scrollRef = useRef(null);
  const languageCode = language?.split("-")[0];

  const getQuestionTitle = (option) =>
    option?.[`title_${languageCode}`] || option?.[languageCode] || option?.title;

  const getSelectionList = (option) =>
    option?.[`selection_title_${languageCode}`] ||
    option?.[`selectionTitle_${languageCode}`] ||
    option?.selection_title;

  const getOptionLabel = (option, index) => {
    const localizedOptions = getSelectionList(option);
    return localizedOptions?.[index] || option.selection_title[index];
  };

  useEffect(() => {
    const response = getCustomStoryOptions({ story_id: storyId });
    response.then((res) => {
      if (res.success) {
        const list = res.data.selection_list;
        setOptionList(list);

        const initialCustomInputs = {};
        const initialIndices = [];
        selectedOptions.forEach((val, idx) => {
          if (val && list[idx]) {
            const isStandard = list[idx].selection_title.some(
              (opt) => opt === val,
            );
            if (!isStandard) {
              initialCustomInputs[idx] = val;
              initialIndices.push(idx);
            }
          }
        });
        setCustomInputs(initialCustomInputs);
      } else {
        console.log(res.error.errMsg);
      }
    });
  }, []);

  const handleSelect = (qIndex, option) => {
    onSelectSound?.();
    if (option === "custom") {
      if (customInputIdx === qIndex) {
        // 이미 커스텀 입력이 활성화된 상태에서 다시 누르면 해제
        setCustomInputIdx(null);
        setSelectedOptions((prev) => {
          const next = [...prev];
          next[qIndex] = "";
          return next;
        });
      } else {
        setCustomInputIdx(qIndex);
        const currentCustomVal = customInputs[qIndex] || "";
        setSelectedOptions((prev) => {
          const next = [...prev];
          next[qIndex] = currentCustomVal;
          return next;
        });
      }
      return;
    }

    setCustomInputIdx(null);
    setSelectedOptions((prev) => {
      const next = [...prev];
      if (next[qIndex] === option) {
        // 이미 선택된 옵션을 다시 누르면 해제
        next[qIndex] = "";
      } else {
        // 새로운 옵션 선택
        next[qIndex] = option;

        // 다음 질문으로 이동
        if (qIndex < optionList.length - 1 && scrollRef.current) {
          const nextIdx = qIndex + 1;
          setTimeout(() => {
            setCurrentIdx(nextIdx);
            const container = scrollRef.current;
            if (container) {
              const questionHeight = container.offsetHeight;
              container.scrollTo({
                top: nextIdx * questionHeight,
                behavior: "smooth",
              });
            }
          }, 100);
        }
      }
      return next;
    });
  };

  const handleCustomInputKeyDown = (e, qIndex) => {
    if (e.key === "Enter") {
      const val = customInputs[qIndex];
      if (!val || val.trim() === "") {
        onAlert(t("alerts.enterContent"));
        return;
      }

      if (WordFilter(val)) {
        onAlert(t("alerts.inappropriateContent"));
        return;
      }

      setSelectedOptions((prev) => {
        const next = [...prev];
        next[qIndex] = val;
        return next;
      });
      setCustomInputIdx(null);

      // 다음 질문으로 이동
      if (qIndex < optionList.length - 1) {
        if (scrollRef.current) {
          const nextIdx = qIndex + 1;
          setCurrentIdx(nextIdx);
          const container = scrollRef.current;
          const questionHeight = container.offsetHeight;
          container.scrollTo({
            top: nextIdx * questionHeight,
            behavior: "smooth",
          });
        }
      } else {
        // 마지막 질문일 경우 전체 완료(handleNext) 호출
        setTimeout(() => {
          handleNext();
        }, 100);
      }
    }
  };

  const handlePrev = () => {
    onPrevSound?.();
    if (currentIdx > 0) {
      const prevIdx = currentIdx - 1;
      setCurrentIdx(prevIdx);
      if (scrollRef.current) {
        const container = scrollRef.current;
        const questionHeight = container.offsetHeight;
        container.scrollTo({
          top: prevIdx * questionHeight,
          behavior: "smooth",
        });
      }
    } else {
      onPrev();
    }
  };

  const handleNext = () => {
    // 1. 현재 질문에 대한 답변이 있는지 확인
    const currentVal = selectedOptions[currentIdx];
    if (!currentVal || currentVal.trim() === "") {
      onAlert(t("alerts.enterContent"));
      return;
    }

    // 2. 현재 질문이 마지막 질문이 아니면 다음 질문으로 스크롤
    if (currentIdx < optionList.length - 1) {
      onNextSound?.();
      const nextIdx = currentIdx + 1;
      setCurrentIdx(nextIdx);
      if (scrollRef.current) {
        const container = scrollRef.current;
        const questionHeight = container.offsetHeight;
        container.scrollTo({
          top: nextIdx * questionHeight,
          behavior: "smooth",
        });
      }
      return;
    }

    // 마지막 질문일 경우 모든 항목이 선택되었는지 확인 (빈 문자열 제외)
    const filledCount = selectedOptions.filter(
      (v) => v && v.trim() !== "",
    ).length;
    if (filledCount < optionList.length) {
      onAlert(t("alerts.enterAll"));
      return;
    }

    for (let i = 0; i < selectedOptions.length; i++) {
      const optionVal = selectedOptions[i];
      if (!optionVal || optionVal.trim() === "") continue;

      const isStandardSelected = optionList[i]?.selection_title.some(
        (opt) => opt === optionVal,
      );

      if (!isStandardSelected && WordFilter(optionVal)) {
        onAlert(t("alerts.inappropriateContent"));
        return;
      }
    }

    onNextSound?.();
    onNext();
  };

  return (
    <div className="arial-wrap">
      <figure className="arial-asset1">
        <img
          src="/images/makingBranch/arial-asset1.svg"
          alt={t("alts.decoration")}
        />
      </figure>
      <figure className="arial-asset2">
        <img
          src="/images/makingBranch/arial-asset1.svg"
          alt={t("alts.decoration")}
        />
      </figure>
      <div className="arial-title">
        <figure>
          <img
            src="/images/makingBranch/arial-title.svg"
            alt={t("alts.titleBackground")}
          />
        </figure>
        <p>{t("titleDescription")}</p>
      </div>
      <div className="arial-inner">
        <div className="progress">
          <progress value="85" max="100" />
          <p>85%</p>
          <figure>
            <img
              src="/images/makingBranch/progress-book.svg"
              alt={t("alts.progress")}
            />
          </figure>
        </div>
        <div className="arial-contents">
          <div className="question-wrap" ref={scrollRef}>
            {optionList.map((option, qIndex) => {
              const isStandardSelected = option.selection_title.some(
                (opt) => opt === selectedOptions[qIndex],
              );
              const isCustomSelected =
                !!selectedOptions[qIndex] && !isStandardSelected;

              const shouldShowInput =
                customInputIdx === qIndex || isCustomSelected;

              return (
                <div key={qIndex} className="question">
                  <h3>{getQuestionTitle(option)}</h3>
                  <div className="divider" />
                  <ul className="options">
                    {option.selection_title.map((opt, oIndex) => (
                      <li
                        key={oIndex}
                        onClick={() => handleSelect(qIndex, opt)}
                        className={
                          customInputIdx !== qIndex &&
                          selectedOptions[qIndex] === opt
                            ? "action"
                            : ""
                        }
                      >
                        <p>
                          <span className="num">{oIndex + 1}.</span>{" "}
                          {getOptionLabel(option, oIndex)}
                        </p>
                      </li>
                    ))}
                    <li
                      className={`custom-li ${
                        customInputIdx === qIndex || isCustomSelected
                          ? "action"
                          : ""
                      }`}
                      onClick={() => handleSelect(qIndex, "custom")}
                    >
                      <p>
                        <span className="num">
                          {option.selection_title.length + 1}.
                        </span>
                        {t("actions.customInput")}
                      </p>
                    </li>
                  </ul>
                  {shouldShowInput && (
                    <div className="custom-input-section">
                      <div className="custom-guide">{t("customGuide")}</div>
                      <div
                        className="custom-input-box"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          placeholder={t("placeholder")}
                          value={customInputs[qIndex] || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomInputs((prev) => ({
                              ...prev,
                              [qIndex]: val,
                            }));
                            setSelectedOptions((prev) => {
                              const next = [...prev];
                              next[qIndex] = val;
                              return next;
                            });
                          }}
                          onKeyDown={(e) => handleCustomInputKeyDown(e, qIndex)}
                          autoFocus={customInputIdx === qIndex}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="btns">
            <figure onClick={handlePrev} className="prev-btn">
              <img
                src={getLocalizedAssetPath("/images/makingBranch/prev-btn.svg")}
                alt={t("alts.prev")}
              />
            </figure>
            <figure onClick={handleNext} className="next-btn">
              <img
                src={getLocalizedAssetPath("/images/makingBranch/next-btn.svg")}
                alt={t("alts.next")}
              />
            </figure>
          </div>
        </div>
      </div>
      <figure className="arial-bottom">
        <img
          src="/images/makingBranch/arial-bottom.svg"
          alt={t("alts.bottomAsset")}
        />
      </figure>
      <figure className="custom-arial">
        <img src="/images/makingBranch/arial.svg" alt={t("alts.character")} />
      </figure>
    </div>
  );
}
